import type { AuthAdapter, AdapterResult, AdapterOptions } from "./types";
import type { AuthProvider } from "../types";

export class FirebaseSocialAdapter implements AuthAdapter {
  readonly provider: AuthProvider;

  constructor(provider: "google" | "apple" | "twitter") {
    this.provider = provider;
  }

  async authenticate(options?: AdapterOptions): Promise<AdapterResult> {
    const { getAuth, signInWithPopup, signInWithRedirect, getRedirectResult } =
      await import("firebase/auth");
    const auth = getAuth();
    const authProvider = await this.createProvider(options);

    if (shouldUseRedirect()) {
      await signInWithRedirect(auth, authProvider);
      const result = await getRedirectResult(auth);
      if (!result) {
        throw new Error("Redirect login returned no result");
      }
      const idToken = await result.user.getIdToken();
      return this.buildResult(result, idToken);
    }

    try {
      const result = await signInWithPopup(auth, authProvider);
      const idToken = await result.user.getIdToken();
      return this.buildResult(result, idToken);
    } catch (error: any) {
      if (isPopupBlocked(error)) {
        await signInWithRedirect(auth, authProvider);
        const result = await getRedirectResult(auth);
        if (!result) {
          throw new Error("Redirect login returned no result");
        }
        const idToken = await result.user.getIdToken();
        return this.buildResult(result, idToken);
      }
      throw error;
    }
  }

  private buildResult(
    result: import("firebase/auth").UserCredential,
    idToken: string,
  ): AdapterResult {
    return {
      authHeaders: { "OS-Firebase-Token": idToken },
      providerUser: {
        email: result.user.email ?? undefined,
        displayName: result.user.displayName ?? undefined,
        photoUrl: result.user.photoURL ?? undefined,
        providerId: result.user.providerData[0]?.providerId,
      },
    };
  }

  private async createProvider(
    options?: AdapterOptions,
  ): Promise<import("firebase/auth").AuthProvider> {
    const { GoogleAuthProvider, OAuthProvider } = await import("firebase/auth");

    switch (this.provider) {
      case "google": {
        const p = new GoogleAuthProvider();
        if (options?.forceAccountSelection) {
          p.setCustomParameters({ prompt: "select_account" });
        }
        return p;
      }
      case "apple":
        return new OAuthProvider("apple.com");
      case "twitter":
        return new OAuthProvider("twitter.com");
      default:
        throw new Error(`Unsupported provider: ${this.provider}`);
    }
  }
}

function shouldUseRedirect(): boolean {
  if (typeof window === "undefined") return false;
  const isStandalone =
    (window.navigator as any).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;
  return isStandalone;
}

function isPopupBlocked(error: any): boolean {
  const code = error?.code ?? "";
  return code === "auth/popup-blocked" || code === "auth/popup-closed-by-user";
}
